import { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw, Contrast, X, Link2 } from 'lucide-react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { WINDOW_PRESETS } from './ImageViewer';

/**
 * Side-by-side comparison of current exam vs prior exam with SYNCHRONIZED
 * windowing (presets + brightness/contrast/invert applied to both panes).
 * Zoom/pan are independent per pane (wheel + drag).
 */

const Pane = ({ title, imageList, testId, filter }) => {
  const [idx, setIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const viewportRef = useRef(null);
  const panState = useRef(null);

  const img = imageList[idx] || imageList[0];

  useEffect(() => {
    const vp = viewportRef.current;
    if (!vp) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((z) => Math.min(8, Math.max(0.5, z + (e.deltaY < 0 ? 0.15 : -0.15))));
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, []);

  const onPointerDown = (e) => {
    panState.current = { startX: e.clientX, startY: e.clientY, baseX: pan.x, baseY: pan.y };
  };
  const onPointerMove = (e) => {
    if (!panState.current) return;
    setPan({
      x: panState.current.baseX + (e.clientX - panState.current.startX),
      y: panState.current.baseY + (e.clientY - panState.current.startY),
    });
  };
  const endPan = () => { panState.current = null; };

  if (!img) return null;

  return (
    <div className="flex-1 min-w-0" data-testid={testId}>
      <div className="flex items-center justify-between px-2 py-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-cyan-300">{title}</span>
        {imageList.length > 1 && (
          <div className="flex gap-1">
            {imageList.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                className={`w-2 h-2 rounded-full transition-colors duration-150 ${i === idx ? 'bg-cyan-400' : 'bg-[hsl(210,14%,25%)] hover:bg-cyan-400/50'}`}
                data-testid={`${testId}-dot-${i}`}
                aria-label={`Image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      <div
        ref={viewportRef}
        className="relative h-[320px] md:h-[380px] bg-black overflow-hidden cursor-grab active:cursor-grabbing rounded-md border border-[hsl(210,14%,16%)]"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endPan}
        onPointerLeave={endPan}
      >
        <div
          className="absolute inset-0 flex items-center justify-center"
          style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
        >
          <img
            src={`data:${img.image_mime || 'image/png'};base64,${img.image_base64}`}
            alt={title}
            draggable={false}
            className="max-w-full max-h-full object-contain select-none"
            style={{ filter }}
            data-testid={`${testId}-image`}
          />
        </div>
      </div>
    </div>
  );
};

export const ComparisonView = ({ images = [], priorImages = [], onExit, t }) => {
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [invert, setInvert] = useState(false);
  const [activePreset, setActivePreset] = useState('default');

  const applyPreset = useCallback((preset) => {
    setBrightness(preset.brightness);
    setContrast(preset.contrast);
    setInvert(false);
    setActivePreset(preset.id);
  }, []);

  const reset = () => applyPreset(WINDOW_PRESETS[0]);

  const filter = `brightness(${brightness}%) contrast(${contrast}%)${invert ? ' invert(1)' : ''}`;

  return (
    <div className="p-3 space-y-3" data-testid="comparison-view">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-[10px] text-teal-300 flex items-center gap-1.5">
          <Link2 size={11} />
          {t('compare.sync_note')}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={onExit}
          className="h-7 text-[11px] gap-1.5"
          data-testid="comparison-exit-button"
        >
          <X size={12} />
          {t('compare.exit')}
        </Button>
      </div>

      {/* Dual panes */}
      <div className="flex flex-col md:flex-row gap-3">
        <Pane title={t('compare.current')} imageList={images} testId="compare-current-pane" filter={filter} />
        <Pane title={t('compare.prior')} imageList={priorImages} testId="compare-prior-pane" filter={filter} />
      </div>

      {/* Shared synchronized windowing controls */}
      <div className="space-y-2.5 rounded-lg bg-[hsl(210,14%,11%)] border border-border p-3">
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="compare-presets-bar">
          <span className="text-[9px] uppercase tracking-wide text-muted-foreground mr-1">{t('viewer.windowing')}</span>
          {WINDOW_PRESETS.map((preset) => (
            <button
              key={preset.id}
              onClick={() => applyPreset(preset)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors duration-150 ${
                activePreset === preset.id
                  ? 'bg-cyan-500 text-[hsl(210,25%,8%)]'
                  : 'bg-[hsl(210,14%,16%)] text-muted-foreground hover:text-foreground hover:bg-[hsl(210,14%,20%)]'
              }`}
              data-testid={`compare-preset-${preset.id}`}
            >
              {t(`viewer.preset_${preset.id}`)}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground w-16 shrink-0">{t('viewer.brightness')}</span>
            <Slider
              value={[brightness]}
              min={30}
              max={220}
              step={5}
              onValueChange={([v]) => { setBrightness(v); setActivePreset('custom'); }}
              className="flex-1"
              data-testid="compare-brightness-slider"
            />
            <span className="text-[10px] text-muted-foreground w-8 text-right">{brightness}%</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground w-16 shrink-0">{t('viewer.contrast')}</span>
            <Slider
              value={[contrast]}
              min={30}
              max={250}
              step={5}
              onValueChange={([v]) => { setContrast(v); setActivePreset('custom'); }}
              className="flex-1"
              data-testid="compare-contrast-slider"
            />
            <span className="text-[10px] text-muted-foreground w-8 text-right">{contrast}%</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setInvert((v) => !v)}
            className={`h-7 text-[11px] gap-1.5 ${invert ? 'border-cyan-500/50 text-cyan-300' : ''}`}
            data-testid="compare-invert-button"
          >
            <Contrast size={12} />
            {t('viewer.invert')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            className="h-7 text-[11px] gap-1.5"
            data-testid="compare-reset-button"
          >
            <RotateCcw size={12} />
            {t('viewer.reset')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
